import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { L, Stars } from "./helpers";
import { I } from "./icons";
import { GOALS } from "./data";
import { useNav } from "./context/NavContext";
import { ProductImg } from "./shared/ProductImg";

/**
 * Render text with the matched query highlighted (case-insensitive).
 * Used by the search autocomplete to mirror iHerb/Amazon behavior.
 */
function Highlight({ text, q }: { text: string; q: string }) {
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "transparent", color: "var(--ac3)", fontWeight: 700, padding: 0 }}>
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export function NutriMartOverlays(s: any) {
  const {
    quickView, setQuickView, lang, t, $, addCart,
    modal, setModal,
    showOnboarding, authed, obStep, setObStep, obGoal, setObGoal,
    obDiets, setObDiets, obWearable, setObWearable, finishOnboarding, setAiGoal,
    searchOpen, setSearchOpen, search, setSearch, recentSearches, setRecentSearches,
    trendingSearches, ingredientSearch, P,
    toasts,
  } = s;
  const { nav } = useNav();

  const QuickViewModal = () => {
    if (!quickView) return null;
    const p = quickView;
    const goal = GOALS.find((g: any) => p.goals.includes(g.id));
    return (
      <motion.div className="qv-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setQuickView(null)}>
        <motion.div className="qv-modal" initial={{ scale: .9, opacity: 0, y: 30 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .9, opacity: 0, y: 30 }} transition={{ type: "spring", damping: 25, stiffness: 350 }} onClick={(e) => e.stopPropagation()}>
          <button className="qv-close" onClick={() => setQuickView(null)}>{I.x}</button>
          <div className="qv-img"><ProductImg src={p.img} alt={L(p.nm, lang)} w={480} /></div>
          <div className="qv-body">
            <div className="pbr">{p.br}</div>
            <h2 style={{ fontFamily: "var(--fs)", fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{L(p.nm, lang)}</h2>
            <div className="prt" style={{ marginBottom: 6 }}><Stars r={p.rt} s={14} /><span>{p.rt} ({p.rv} {t.reviews})</span></div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: "var(--ac3)" }}>{$(p.pr)}</span>
              {p.cpr && <span style={{ fontSize: 13, color: "var(--tx3)", textDecoration: "line-through" }}>{$(p.cpr)}</span>}
              {p.cpr && <span style={{ padding: "2px 8px", borderRadius: 100, background: "#FFEBEE", color: "var(--rd)", fontSize: 10, fontWeight: 700 }}>-{Math.round((1 - p.pr / p.cpr) * 100)}%</span>}
            </div>
            <p style={{ fontSize: 12.5, color: "var(--tx2)", lineHeight: 1.6, marginBottom: 10 }}>{L(p.desc, lang)}</p>
            <div className="qv-tags">{p.goals.map((g: string) => { const gl = GOALS.find((x: any) => x.id === g); return gl ? <span key={g} className="dt" style={{ padding: "3px 9px", borderRadius: 100, fontSize: 10, fontWeight: 600, background: `${gl.c}18`, color: gl.c, border: `1px solid ${gl.c}30` }}>{gl.i} {t[g]}</span> : null; })}</div>
            {goal && <div className="aid" style={{ marginTop: 8 }}><h4 style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ac3)", display: "flex", alignItems: "center", gap: 4 }}><span style={{ display: "inline-flex" }}>{I.spark}</span>{t.whyFits}</h4><p style={{ fontSize: 11, color: "var(--tx2)" }}>{L(p.ben, lang)[0]}</p></div>}
            <div className="qv-actions"><button className="btn bp" style={{ flex: 1 }} onClick={() => { addCart(p); setQuickView(null); }}>{t.addToCart} — {$(p.pr)}</button><button className="btn bo" onClick={() => { setQuickView(null); nav("detail", p); }}>{lang === "es" ? "Ver detalle" : "View Details"} →</button></div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <>
      {modal && <div className="mov" onClick={() => setModal(null)}><div className="mdl" onClick={(e) => e.stopPropagation()}><h3>{modal.title}</h3><p>{modal.text}</p><div className="mbs"><button className="btn bg" onClick={() => setModal(null)}>{t.cancel}</button><button className="btn bp" onClick={modal.onConfirm}>{t.confirm}</button></div></div></div>}

      {showOnboarding && authed && <div className="ob-overlay"><div className="ob-card">
        <div className="ob-progress">{[0, 1, 2, 3].map(i => <div key={i} className={`ob-dot ${i <= obStep ? "on" : ""}`} />)}</div>
        {obStep === 0 && <><h2>{t.onboarding}</h2><p className="ob-sub">{t.obStep} 1 {t.obOf} 3 — {t.obGoal}</p>
          <div className="ob-options">{GOALS.map((g: any) => <div key={g.id} className={`ob-opt ${obGoal === g.id ? "on" : ""}`} onClick={() => setObGoal(g.id)}><span className="ob-ic">{g.i}</span><span className="ob-lb">{t[g.id]}</span></div>)}</div>
          <div style={{ display: "flex", gap: 8 }}><button className="btn bg" onClick={finishOnboarding}>{t.obSkip}</button><button className="btn bp" style={{ flex: 1 }} onClick={() => { if (obGoal) setObStep(1); }}>{t.obNext} →</button></div></>}
        {obStep === 1 && <><h2>{t.onboarding}</h2><p className="ob-sub">{t.obStep} 2 {t.obOf} 3 — {t.obDiet}</p>
          <div className="ob-options">{(lang === "es" ? ["Vegano", "Keto", "Bajo en Azúcar", "Sin Gluten", "Sin Lactosa", "Orgánico"] : ["Vegan", "Keto", "Low Sugar", "Gluten Free", "Lactose Free", "Organic"]).map(d => <div key={d} className={`ob-opt ${obDiets.includes(d) ? "on" : ""}`} onClick={() => setObDiets((p: string[]) => p.includes(d) ? p.filter(x => x !== d) : [...p, d])}><span className="ob-lb">{d}</span></div>)}</div>
          <div style={{ display: "flex", gap: 8 }}><button className="btn bg" onClick={() => setObStep(0)}>← {t.back}</button><button className="btn bp" style={{ flex: 1 }} onClick={() => setObStep(2)}>{t.obNext} →</button></div></>}
        {obStep === 2 && <><h2>{t.onboarding}</h2><p className="ob-sub">{t.obStep} 3 {t.obOf} 3 — {t.obWearable}</p>
          <div className="ob-options">{[{ id: "oura", nm: "Oura Ring", ic: "💍" }, { id: "apple", nm: "Apple Watch", ic: "⌚" }, { id: "whoop", nm: "WHOOP", ic: "📿" }, { id: "none", nm: lang === "es" ? "Ninguno" : "None", ic: "❌" }].map(w => <div key={w.id} className={`ob-opt ${obWearable === w.id ? "on" : ""}`} onClick={() => setObWearable(w.id)}><span className="ob-ic">{w.ic}</span><span className="ob-lb">{w.nm}</span></div>)}</div>
          <div style={{ display: "flex", gap: 8 }}><button className="btn bg" onClick={() => setObStep(1)}>← {t.back}</button><button className="btn bp" style={{ flex: 1 }} onClick={() => { setObStep(3); if (obGoal) setAiGoal(obGoal); }}>{t.obNext} →</button></div></>}
        {obStep === 3 && <div className="ob-done"><div className="ob-chk">✅</div><h2>{t.obDone}</h2><p className="ob-sub">{t.obDoneMsg}</p><button className="btn bp bf" onClick={finishOnboarding}>{t.obFinish} →</button></div>}
      </div></div>}

      {searchOpen && <div className="srch-ov"><div className="srch-top">
        <SearchAutocomplete
          search={search}
          setSearch={setSearch}
          setSearchOpen={setSearchOpen}
          recentSearches={recentSearches}
          setRecentSearches={setRecentSearches}
          trendingSearches={trendingSearches}
          ingredientSearch={ingredientSearch}
          P={P}
          lang={lang}
          t={t}
          $={$}
          addCart={addCart}
          nav={nav}
        />
      </div></div>}

      {toasts.map((ts: any) => <div key={ts.id} className="tst">{ts.m}</div>)}
      <AnimatePresence>{quickView && <QuickViewModal />}</AnimatePresence>
    </>
  );
}

/**
 * Real autocomplete with:
 *  - debounced match against name / brand / ingredients
 *  - top-8 suggestions with highlighted matched text + image + price
 *  - keyboard ↑/↓/Enter navigation (active row scrolls into view)
 */
function SearchAutocomplete({
  search, setSearch, setSearchOpen, recentSearches, setRecentSearches,
  trendingSearches, ingredientSearch, P, lang, t, $, addCart, nav,
}: any) {
  const [active, setActive] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const q = search.trim().toLowerCase();
  const results = React.useMemo(() => {
    if (!q) return [] as any[];
    return P.filter((p: any) =>
      L(p.nm, lang).toLowerCase().includes(q) ||
      p.br.toLowerCase().includes(q) ||
      L(p.ing, lang).toLowerCase().includes(q)
    ).slice(0, 8);
  }, [P, q, lang]);

  // Reset selection when query or result set changes.
  React.useEffect(() => { setActive(0); }, [q, results.length]);

  // Keep the active row visible inside the scrollable results list.
  React.useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const commitQuery = (qStr: string) => {
    setRecentSearches((prev: string[]) => [qStr, ...prev.filter((x) => x !== qStr)].slice(0, 5));
    setSearchOpen(false);
    nav("products");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!results.length) {
      if (e.key === "Enter" && q) { e.preventDefault(); commitQuery(search); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(results.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const p = results[active];
      if (p) {
        setRecentSearches((prev: string[]) => [search, ...prev.filter((x) => x !== search)].slice(0, 5));
        setSearchOpen(false);
        nav("detail", p);
      }
    } else if (e.key === "Escape") {
      e.preventDefault(); setSearchOpen(false);
    }
  };

  return (
    <>
      <div className="srch-input">
        {I.search}
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t.searchPh}
          onKeyDown={onKeyDown}
          aria-autocomplete="list"
          aria-activedescendant={results[active] ? `srch-row-${active}` : undefined}
        />
        <button className="srch-close" onClick={() => setSearchOpen(false)}>{I.x}</button>
      </div>

      {!search && (
        <>
          <div className="srch-sec"><h4>{t.searchRecent}</h4><div className="srch-tags">{recentSearches.map((s2: string) => <button key={s2} className="srch-tag" onClick={() => { setSearch(s2); setSearchOpen(false); nav("products"); }}>{s2}</button>)}</div></div>
          <div className="srch-sec"><h4>🔥 {t.searchTrending}</h4><div className="srch-tags">{trendingSearches.map((s2: string) => <button key={s2} className="srch-tag" onClick={() => { setSearch(s2); setSearchOpen(false); nav("products"); }}>{s2}</button>)}</div></div>
          <div className="srch-sec"><h4>🧪 {t.searchByIngredient}</h4><div className="srch-tags">{ingredientSearch.map((s2: string) => <button key={s2} className="srch-tag" onClick={() => { setSearch(s2); setSearchOpen(false); nav("products"); }}>{s2}</button>)}</div></div>
        </>
      )}

      {search && (
        <div className="srch-results" ref={listRef} role="listbox">
          <p style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 10 }}>
            {results.length} {t.searchResults} "{search}"
          </p>
          {results.map((p: any, i: number) => (
            <div
              key={p.id}
              id={`srch-row-${i}`}
              data-row={i}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                borderBottom: "1px solid var(--bd2)", cursor: "pointer",
                background: i === active ? "var(--bg2)" : "transparent",
                borderRadius: 6,
              }}
              onClick={() => {
                setRecentSearches((prev: string[]) => [search, ...prev.filter((x) => x !== search)].slice(0, 5));
                setSearchOpen(false);
                nav("detail", p);
              }}
            >
              <ProductImg src={p.img} alt="" w={80} style={{ width: 40, height: 40, objectFit: "contain" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <Highlight text={L(p.nm, lang)} q={search} />
                </div>
                <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                  <Highlight text={p.br} q={search} /> · {$(p.pr)}
                </div>
              </div>
              <button className="btn bp bs" onClick={(e) => { e.stopPropagation(); addCart(p); }}>{t.add}</button>
            </div>
          ))}
          {results.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--tx3)", padding: "16px 0" }}>
              {lang === "es" ? "Sin resultados. Prueba con otra palabra." : "No results. Try another keyword."}
            </div>
          )}
        </div>
      )}
    </>
  );
}
